import { createContext, useContext, useEffect, useMemo, useState, type ReactNode } from 'react'
import { useAuth } from './auth'
import type { CountryAccess } from '@/types/api'

interface CountryState {
  /** The country every country-scoped screen filters by. Never undefined once signed in. */
  current: CountryAccess | undefined
  available: CountryAccess[]
  select: (countryId: string) => void
}

const CountryContext = createContext<CountryState | undefined>(undefined)

const STORAGE_KEY = 'quotehero.country'

/**
 * Which country the user is working in.
 *
 * Country scopes almost everything in this product - pricing, letterhead, terms,
 * approval chain, quotation numbering - so it is a first-class piece of app state
 * shown permanently in the chrome rather than a filter buried in each screen.
 * Quoting a Singapore customer on a Malaysian letterhead is the mistake this
 * product exists to prevent, and it starts with not knowing which country you
 * are in.
 */
export function CountryProvider({ children }: { children: ReactNode }) {
  const { countries } = useAuth()
  const [selectedId, setSelectedId] = useState<string | undefined>(undefined)

  useEffect(() => {
    if (countries.length === 0) {
      setSelectedId(undefined)
      return
    }

    let remembered: string | null = null
    try {
      remembered = localStorage.getItem(STORAGE_KEY)
    } catch {
      /* storage disabled; fall through to the default */
    }

    // Remembered, then the user's own default, then the first grant. A remembered
    // country the user has since lost access to is ignored rather than trusted.
    const resolved =
      countries.find((c) => c.countryId === remembered) ??
      countries.find((c) => c.isDefault) ??
      countries[0]

    setSelectedId(resolved?.countryId)
  }, [countries])

  const value = useMemo<CountryState>(() => {
    const current = countries.find((c) => c.countryId === selectedId)

    return {
      current,
      available: countries,
      select: (countryId: string) => {
        if (!countries.some((c) => c.countryId === countryId)) return

        setSelectedId(countryId)
        try {
          localStorage.setItem(STORAGE_KEY, countryId)
        } catch {
          /* the choice still holds for this session */
        }
      },
    }
  }, [countries, selectedId])

  return <CountryContext.Provider value={value}>{children}</CountryContext.Provider>
}

export function useCountry(): CountryState {
  const context = useContext(CountryContext)
  if (!context) throw new Error('useCountry must be used inside CountryProvider')

  return context
}
